import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../../utils/api-handler.util'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import { Link, useSearchParams } from 'react-router-dom'
import ProductCard from '../../../components/products/ProductCard'
import useProductCategories from '../../../hooks/useProductCategories'
import { useAuth } from '../../../contexts/AuthContext'
import USER_TYPE from '../../../constants/userType'
import PageTitle from '../../../components/common/PageTitle'
import Pagination from '../../../components/common/Pagination'
import SeoHelmet from '../../../components/common/SeoHelmet'
import CategoriesFilter from '../../../components/common/CategoriesFilter'

const ProductsPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const { categories } = useProductCategories()
  const { currentUser } = useAuth()
  const userType = currentUser?.userType || USER_TYPE.guest
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category') || null

  const { data: productsData } = useQuery({
    queryKey: QueryKeys.categoryProducts(category, userType, currentPage),
    queryFn: () =>
      handleGetRequest('/products/retail', {
        page: currentPage,
        categorySlug: category,
      }),
  })

  const allProducts = productsData?.data || []
  const paginationMeta = productsData?.meta

  const selectedCategory = categories.find((cat) => cat.categorySlug === category)

  return (
    <div className="main-container">
      <SeoHelmet
        title={selectedCategory ? `${selectedCategory.name} Products` : 'All Products'}
        description={
          selectedCategory
            ? `Shop ${selectedCategory.name.toLowerCase()} products from trusted Eastleigh retailers. Find quality items at competitive prices.`
            : "Browse all products from Eastleigh's top retailers. Discover fashion, electronics, home goods and more at great prices."
        }
        keywords={
          selectedCategory
            ? `${
                selectedCategory.name
              }, Eastleigh ${selectedCategory.name.toLowerCase()}, Kenya ${selectedCategory.name.toLowerCase()}, online shopping`
            : 'Eastleigh products, online shopping Kenya, retail products, marketplace, Sokooni products'
        }
        url={selectedCategory ? `/products?category=${category}` : '/products'}
      />

      <div className="w-full">
        <PageTitle>All Products</PageTitle>
        <CategoriesFilter setPage={setCurrentPage} />

        <div className="mt-6 lg:mt-8">
          <div>
            {allProducts.length === 0 ? (
              <div className="text-center py-12 lg:py-16">
                <div className="text-gray-500 text-base lg:text-lg">
                  No products found for this category.
                </div>
              </div>
            ) : (
              <div className="space-y-3 lg:space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
                  {allProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={`/retailers/${product.shopSlug}/products/${product.productSlug}`}
                      style={{ textDecoration: 'none' }}
                      className="block hover:transform hover:scale-[1.02] transition-transform duration-200"
                    >
                      <ProductCard product={product} catalogProducts={true} />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {paginationMeta && (
              <div className="mt-8 lg:mt-12">
                <Pagination paginationMeta={paginationMeta} onPageChange={setCurrentPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductsPage
